import React, { useState } from "react";
import Navbar from "./Navbar";
import Home from "./Home";
import PublicGovernmentRequestForm from "./PublicGovernmentRequestForm";
import SuccessMessage from "./SuccessMessage";
import {
  addPublicGovernmentRequest,
  getRequestTypes,
} from "common/services/govt-requests.service";
import { useFormik } from "formik";
import * as Yup from "yup";
import { extractFieldErrors } from "common/lib/fieldErrors";
import { Container } from "reactstrap";

const GovernmentRequestForm = () => {
  document.title = "Government Request Form | CIRT";

  window.onscroll = function () {
    scrollFunction();
  };

  const scrollFunction = () => {
    const element = document.getElementById("back-to-top");
    if (element) {
      if (
        document.body.scrollTop > 100 ||
        document.documentElement.scrollTop > 100
      ) {
        element.style.display = "block";
      } else {
        element.style.display = "none";
      }
    }
  };

  const toTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<any>([]);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const handleStartNewForm = () => {
    setShowSuccess(false);
    setFieldErrors([]);
    validation.resetForm();

    // Scroll to form section
    setTimeout(() => {
      const formSection = document.querySelector(".bg-light");
      if (formSection) {
        const navbarHeight = 80;
        const elementPosition =
          formSection.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      requestTitle: "",
      requestType: "NTP",
      requestDescription: "",
      isStaffRequest: false,
      noOfPositionsRequested: "",
      positionTitle: "",
      positionType: {
        value: "",
        label: "",
      },
      requestedSalary: null,
      totalValueOfRequest: null,
      periodInMonths: "",
      dateOfRequest: "",
      startDate: "",
      endDate: "",

      contactPerson: "",
      documentAttachments: [],
      fundingStatus: "",
      reviewOfRequest: "",
      agency: {
        value: "",
        label: "",
      },
      region: {
        value: "",
        label: "",
      },
      mda: {
        value: "",
        label: "",
      },
      entity: {
        value: "",
        label: "",
      },
      nameOfRequester: "",
      objectiveOfRequest: "",
      formOfGovernmentRequest: {
        value: "",
        label: "",
      },
      expectedOutputs: "",
      additionalDetails: "",

      selectedFiles: [],
    },
    validationSchema: Yup.object({
      requestTitle: Yup.string().when("requestType", {
        is: (val: string) => val === "Other",
        then: (schema) => schema.required("Please enter request title"),
        otherwise: (schema) => schema.notRequired(),
      }),
      requestType: Yup.string(),
      noOfPositionsRequested: Yup.number().when("isStaffRequest", {
        is: true,
        then: (schema) => schema.required("Please enter number of positions"),
      }),
      positionTitle: Yup.string().when("isStaffRequest", {
        is: true,
        then: (schema) => schema.required("Please enter position title"),
      }),
      positionType: Yup.object().when("isStaffRequest", {
        is: true,
        then: (schema) =>
          schema.shape({
            value: Yup.string().required("Please select position type"),
          }),
      }),
      requestedSalary: Yup.number().when(["isStaffRequest", "requestType"], {
        is: (isStaffRequest: boolean, requestType: string) =>
          isStaffRequest === true && requestType === "Other",
        then: (schema) => schema.required("Please enter requested salary"),
        otherwise: (schema) => schema.notRequired(),
      }),
      totalValueOfRequest: Yup.number().required("Please enter total value"),
      periodInMonths: Yup.number().when("isStaffRequest", {
        is: (val: string) => val === "Other",
        then: (schema) => schema.required("Please enter period in months"),
        otherwise: (schema) => schema.notRequired(),
      }),
      startDate: Yup.date().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please select start date"),
        otherwise: (schema) => schema.notRequired(),
      }),

      endDate: Yup.date().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please select end date"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dateOfRequest: Yup.date().required("Please select date of request"),

      contactPerson: Yup.string(),
      fundingStatus: Yup.string(),
      reviewOfRequest: Yup.string().when(["requestType", "isStaffRequest"], {
        is: (requestType: string, isStaffRequest: boolean) =>
          requestType === "Other" && isStaffRequest === true,
        then: (schema) => schema.required("Please enter review of request"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // entity: Yup.object().shape({
      //   value: Yup.string().required("Please select entity"),
      // }),
      // agency: Yup.object().when("entity.value", {
      //   is: (val: string) => val === "UN" || val === "Donor",
      //   then: (schema) =>
      //     schema.shape({
      //       value: Yup.string().required("Please select agency"),
      //     }),
      //   otherwise: (schema) =>
      //     schema.shape({
      //       value: Yup.string().notRequired(),
      //     }),
      // }),
      agency: Yup.object().shape({
        value: Yup.string().required("Please select agency"),
      }),
      region: Yup.object().shape({
        value: Yup.string().required("Please select region"),
      }),
      mda: Yup.object().shape({
        value: Yup.string().required("Please select MDA"),
      }),

      nameOfRequester: Yup.string().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please enter name of requester"),
        otherwise: (schema) => schema.notRequired(),
      }),
      objectiveOfRequest: Yup.string().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please enter objective of request"),
        otherwise: (schema) => schema.notRequired(),
      }),
      formOfGovernmentRequest: Yup.object()
        .shape({
          value: Yup.string(),
        })
        .when("requestType", {
          is: (val: string) => val !== "Other",
          then: (schema) =>
            schema.shape({
              value: Yup.string().required(
                "Please select form of government request"
              ),
            }),
          otherwise: (schema) =>
            schema.shape({
              value: Yup.string().notRequired(),
            }),
        }),
      expectedOutputs: Yup.string(),
      additionalDetails: Yup.string(),
      selectedFiles: Yup.array(),
      // .min(1, "Atleast one file is required")
      // .required("Required"),
    }),
    onSubmit: async (values: any) => {
      try {
        let data = {};
        const requestTypeResponse = await getRequestTypes({
          filters: {
            code: values.requestType,
          },
        });
        const requestTypeId = requestTypeResponse?.data[0];

        const startDate = values.startDate;
        const endDate = values.endDate;

        let monthDifference = null;

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);

          monthDifference =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());

          // If you want to include partial months based on day difference:
          if (end.getDate() < start.getDate()) {
            monthDifference -= 1;
          }
        }

        if (values.requestType === "Other") {
          data = {
            requestTitle: values?.requestTitle || null,
            typeOfRequest: requestTypeId || null,
            requestDescription: values?.requestDescription || null,
            isStaffRequest: values?.isStaffRequest || null,
            noOfPositionsRequested: `${values?.noOfPositionsRequested}` || null,
            positionTitle: values?.positionTitle || null,
            requestedSalary: values?.requestedSalary || null,
            totalValueOfRequest: values?.totalValueOfRequest || null,
            periodInMonths: values?.periodInMonths?.toString() || null,
            dateOfRequest:
              (values?.dateOfRequest &&
                values?.dateOfRequest?.toLocaleDateString("en-CA")) ||
              null,
            startDate: null,
            endDate: null,
            contactPerson: null,
            fundingStatus: values?.fundingStatus || null,
            reviewOfRequest: values?.reviewOfRequest || null,
            agency: values?.agency?.value || null,
            region: values?.region?.value || null,
            mda: values?.mda?.value || null,
            entity: values?.entity?.label || null,
            positionType: values?.positionType?.value || null,
            formOfGovernmentRequest: null,
            nameOfRequester: null,
            objectiveOfRequest: null,
            expectedOutputs: null,
            additionalDetails: values?.additionalDetails || null,
          };
        } else {
          data = {
            requestTitle: null,
            typeOfRequest: requestTypeId || null,
            requestDescription: null,
            isStaffRequest: null,
            noOfPositionsRequested: null,
            positionTitle: null,
            requestedSalary: null,
            totalValueOfRequest: values?.totalValueOfRequest || null,
            periodInMonths: `${monthDifference}`,
            dateOfRequest:
              (values?.dateOfRequest &&
                values?.dateOfRequest?.toLocaleDateString("en-CA")) ||
              null,
            startDate:
              (values?.startDate &&
                values.startDate?.toLocaleDateString("en-CA")) ||
              null,
            endDate:
              (values?.endDate &&
                values?.endDate?.toLocaleDateString("en-CA")) ||
              null,
            contactPerson: values?.contactPerson || null,
            fundingStatus: values?.fundingStatus || null,
            reviewOfRequest: null,
            agency: values?.agency?.value || null,
            mda: values?.mda?.value || null,
            entity: values?.entity?.label || null,
            positionType: null,
            formOfGovernmentRequest:
              values?.formOfGovernmentRequest?.value || null,
            nameOfRequester: values?.nameOfRequester || null,
            objectiveOfRequest: values?.objectiveOfRequest || null,
            expectedOutputs: values?.expectedOutputs || null,
            additionalDetails: values?.additionalDetails || null,
          };
        }

        const formData = new FormData();
        values.selectedFiles.forEach((file: any) => {
          formData.append("files.documentAttachments", file, file.name);
        });
        formData.append("data", JSON.stringify(data));

        setLoading(true);
        await addPublicGovernmentRequest(formData);

        // Show success message and reset form
        setShowSuccess(true);
        validation.resetForm();
        setFieldErrors([]);

        // Scroll to top to show success message
        // window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error: any) {
        const errors = extractFieldErrors(error?.response);
        setFieldErrors(errors);
      } finally {
        setLoading(false);
      }
    },
  });

  console.log("Validation errors", validation.errors);

  return (
    <React.Fragment>
      <div className="layout-wrapper landing">
        <Navbar />
        <Home />
        <div className="bg-light pt-5">
          <Container>
            {showSuccess ? (
              <SuccessMessage onStartNewForm={handleStartNewForm} />
            ) : (
              <PublicGovernmentRequestForm
                loading={loading}
                validation={validation}
                fieldErrors={fieldErrors}
              />
            )}
          </Container>
        </div>

        <button
          onClick={() => toTop()}
          className="btn btn-danger btn-icon landing-back-top"
          id="back-to-top"
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </div>
    </React.Fragment>
  );
};

export default GovernmentRequestForm;
